import { logAuthEvent } from './sentry';

export interface TokenValidationResult {
  isValid: boolean;
  shouldRefresh: boolean;
  timeRemaining?: number;
}

export const validateTokenExpiry = (token: string | null): TokenValidationResult => {
  if (!token) {
    return { isValid: false, shouldRefresh: true };
  }

  try {
    // Parse JWT payload (basic validation without verification)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);
    const exp = payload.exp;
    
    if (!exp) {
      return { isValid: false, shouldRefresh: true };
    }

    const timeRemaining = exp - now;
    
    // Token expired
    if (timeRemaining <= 0) {
      logAuthEvent('token_expired', { timeRemaining, exp, now });
      return { isValid: false, shouldRefresh: true };
    }
    
    // Token expires in less than 2 minutes - proactively refresh
    if (timeRemaining < 120) {
      logAuthEvent('token_expiring_soon', { timeRemaining, exp, now });
      return { isValid: true, shouldRefresh: true, timeRemaining };
    }
    
    return { isValid: true, shouldRefresh: false, timeRemaining };
  } catch (error: any) {
    logAuthEvent('token_parse_error', { error: error.message });
    return { isValid: false, shouldRefresh: true };
  }
};

export const checkCookieSupport = (): boolean => {
  try {
    // Test if cookies are enabled
    document.cookie = 'test_cookie=test; SameSite=Lax';
    const cookieEnabled = document.cookie.indexOf('test_cookie') !== -1;
    
    // Clean up test cookie
    document.cookie = 'test_cookie=; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
    
    return cookieEnabled;
  } catch (error) {
    return false;
  }
};

export const diagnoseAuthIssue = (): Record<string, any> => {
  const diagnosis = {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    cookiesEnabled: checkCookieSupport(),
    localStorage: {
      available: typeof Storage !== 'undefined',
      hasUser: !!localStorage.getItem('cached_user'),
      userKeys: Object.keys(localStorage).filter(k => k.includes('user'))
    },
    cookies: {
      all: document.cookie,
      hasRefreshToken: document.cookie.includes('refresh_token'),
      cookieCount: document.cookie.split(';').length
    },
    location: {
      href: window.location.href,
      origin: window.location.origin,
      protocol: window.location.protocol
    },
    connection: {
      onLine: navigator.onLine,
      connectionType: (navigator as any).connection?.effectiveType || 'unknown'
    }
  };

  logAuthEvent('auth_diagnosis', diagnosis);
  return diagnosis;
};

export const clearAllAuthData = () => {
  // Clear localStorage
  const keysToRemove = Object.keys(localStorage).filter(key => 
    key.includes('user') || key.includes('auth') || key.includes('token')
  );
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
  
  // Clear auth-related cookies (limited by SameSite/HttpOnly)
  const authCookies = ['access_token', 'refresh_token', 'user_session'];
  authCookies.forEach(cookieName => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=None; Secure`;
  });
  
  logAuthEvent('auth_data_cleared', { clearedKeys: keysToRemove });
};